import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { CommentList } from '../../components/CommentList/CommentList';
import { AuthRequiredModal } from '../../components/AuthRequiredModal/AuthRequiredModal';
import { ImageCarousel } from '../../components/ImageCarousel/ImageCarousel';
import { isValidCarType, isValidPerformanceType } from '../../constants/spotCategories';
import { getCountryFlagImageUrl, getCountryName } from '../../constants/countries';
import type { Comment } from '../../types';
import styles from './SpotDetail.module.css';

function formatDate(iso: string) {
  return new Date(iso).toLocaleString();
}

export function SpotDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const { spots, getAuthor, likeSpot, unlikeSpot, beautifulSpot, unBeautifulSpot, coolSpot, unCoolSpot, sweetPhotoSpot, unSweetPhotoSpot, addComment, favoriteLists, addToFavorites, removeFromFavorites, fetchSpot } = useData();
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [replyTarget, setReplyTarget] = useState<Comment | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const commentsSectionRef = useRef<HTMLDivElement | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);

  const spotFromState = location.state?.spot as typeof spots[0] | undefined;
  const cameFromSpotForm = location.state?.fromSpotForm === true;
  const spotFromContext = id ? spots.find((s) => s.id === id) : null;
  const spot = (spotFromState?.id === id ? spotFromState : null) ?? spotFromContext;
  const author = spot?.author ?? (spot ? getAuthor(spot.authorId) : null);
  const isAuthor = spot && currentUser && spot.authorId === currentUser.id;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDetailLoading(true);
    fetchSpot(id, { incrementView: true })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchSpot, id]);

  useEffect(() => {
    if (!spot) return;

    if (location.hash === '#comments') {
      const frame = window.requestAnimationFrame(() => {
        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        commentInputRef.current?.focus({ preventScroll: true });
      });

      return () => window.cancelAnimationFrame(frame);
    }

    if (!location.hash.startsWith('#comment-')) return;

    let timeoutId: number | undefined;
    let cancelled = false;
    let attempts = 0;

    const scrollToComment = () => {
      if (cancelled) return;

      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (attempts >= 10) return;
      attempts += 1;
      timeoutId = window.setTimeout(scrollToComment, 120);
    };

    const frame = window.requestAnimationFrame(scrollToComment);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [location.hash, spot, spot?.comments.length, isAuthenticated]);

  useEffect(() => {
    if (!spot || !replyTarget) return;
    if (spot.comments.some((comment) => comment.id === replyTarget.id)) return;
    setReplyTarget(null);
  }, [replyTarget, spot]);

  if (!spot) {
    return (
      <div className={styles.page}>
        <p>{detailLoading ? 'Loading spot…' : 'Spot not found.'}</p>
        <Link to="/">Back to feed</Link>
      </div>
    );
  }

  const liked = currentUser && spot.likes.includes(currentUser.id);
  const beautiful = currentUser && (spot.beautiful ?? []).includes(currentUser.id);
  const cool = currentUser && (spot.cool ?? []).includes(currentUser.id);
  const sweetPhoto = currentUser && (spot.sweetPhoto ?? []).includes(currentUser.id);
  const carLabel = [spot.car.make, spot.car.model, spot.car.generation, spot.car.trim, spot.car.yearRange].filter(Boolean).join(' ');

  const requireAuth = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return true;
    }
    return false;
  };
  const handleLike = () => {
    if (requireAuth() || !currentUser) return;
    if (liked) unlikeSpot(spot.id, currentUser.id);
    else likeSpot(spot.id, currentUser.id);
  };
  const handleBeautiful = () => {
    if (requireAuth() || !currentUser) return;
    if (beautiful) unBeautifulSpot(spot.id, currentUser.id);
    else beautifulSpot(spot.id, currentUser.id);
  };
  const handleCool = () => {
    if (requireAuth() || !currentUser) return;
    if (cool) unCoolSpot(spot.id, currentUser.id);
    else coolSpot(spot.id, currentUser.id);
  };
  const handleSweetPhoto = () => {
    if (requireAuth() || !currentUser) return;
    if (sweetPhoto) unSweetPhotoSpot(spot.id, currentUser.id);
    else sweetPhotoSpot(spot.id, currentUser.id);
  };

  const replyTargetAuthor = replyTarget?.author ?? (replyTarget ? getAuthor(replyTarget.authorId) : undefined);

  const focusCommentInput = () => {
    window.requestAnimationFrame(() => {
      commentInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      commentInputRef.current?.focus({ preventScroll: true });
    });
  };

  const clearReplyTarget = () => {
    setReplyTarget(null);
  };

  const handleReplyToComment = (comment: Comment) => {
    if (requireAuth()) return;
    setReplyTarget(comment);
    focusCommentInput();
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (requireAuth() || !currentUser || !commentText.trim() || commentSubmitting) return;
    setCommentSubmitting(true);
    try {
      await addComment(spot.id, {
        authorId: currentUser.id,
        body: commentText.trim(),
        replyToCommentId: replyTarget?.id,
      });
      setCommentText('');
      setReplyTarget(null);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const inList = (listId: string) => {
    const list = favoriteLists.find((f) => f.id === listId);
    return list?.spotIds.includes(spot.id) ?? false;
  };

  const toggleFavorite = (listId: string) => {
    if (inList(listId)) removeFromFavorites(listId, spot.id);
    else addToFavorites(listId, spot.id);
  };

  const goBack = () => {
    if (cameFromSpotForm) {
      navigate('/');
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className={styles.page}>
      <button type="button" onClick={goBack} className={styles.back}>← Back</button>

      <article className={styles.card}>
        <div className={styles.header}>
          <Link to={`/user/${spot.authorId}`} className={styles.author}>
            <span className={styles.avatar}>{author?.displayName?.charAt(0) ?? '?'}</span>
            <div>
              <span className={styles.authorName}>{author?.displayName ?? 'Unknown'}</span>
              <span className={styles.meta}>
                {[spot.city, spot.event].filter(Boolean).join(' · ') || 'Shared a spot'}
                {' · '}
                {formatDate(spot.createdAt)}
              </span>
            </div>
          </Link>
          {isAuthor && (
            <Link to={`/spot/${spot.id}/edit`} className={styles.editLink}>Edit</Link>
          )}
        </div>

        <div className={styles.imageContainer}>
          <div className={styles.imageWrap}>
            <ImageCarousel
              images={spot.images?.length ? spot.images : (spot.imageUrl ? [spot.imageUrl] : [])}
              alt={carLabel}
              wrapClassName={styles.imageWrapInner}
              imageClassName={styles.image}
            />
          </div>
          {spot.countryCode && (
            <span className={styles.countryFlag}>
              <img
                src={getCountryFlagImageUrl(spot.countryCode)}
                alt={getCountryName(spot.countryCode) || spot.countryCode}
                title={`Spotted in ${[spot.city, getCountryName(spot.countryCode) || spot.countryCode].filter(Boolean).join(', ')}`}
                className={styles.countryFlagImg}
              />
            </span>
          )}
        </div>
        {spot.caption && <p className={styles.caption}>{spot.caption}</p>}
        <div className={styles.body}>
          <h1 className={styles.carTitle}>{carLabel}</h1>
          {(isValidCarType(spot.car.bodyStyle) || isValidPerformanceType(spot.car.performanceType)) && (
            <div className={styles.badges}>
              {isValidCarType(spot.car.bodyStyle) && <span className={styles.badge}>{spot.car.bodyStyle}</span>}
              {isValidPerformanceType(spot.car.performanceType) && <span className={styles.badge}>{spot.car.performanceType}</span>}
            </div>
          )}
          {(spot.car.generation || spot.car.trim || spot.car.mods || spot.car.wheels || spot.car.userNotes) && (
            <ul className={styles.details}>
              {spot.car.generation && <li>Generation: {spot.car.generation}</li>}
              {spot.car.trim && <li>Trim: {spot.car.trim}</li>}
              {spot.car.mods && <li>Mods: {spot.car.mods}</li>}
              {spot.car.wheels && <li>Wheels: {spot.car.wheels}</li>}
              {spot.car.userNotes && <li>{spot.car.userNotes}</li>}
            </ul>
          )}
          {spot.location && <p className={styles.location}>📍 {spot.location}</p>}
          {spot.taggedOwnerId && (
            <p className={styles.tagged}>Tagged: {getAuthor(spot.taggedOwnerId)?.displayName ?? 'User'}</p>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={liked ? styles.likeActive : styles.likeBtn}
            onClick={handleLike}
            title="Like"
          >
            ♥ {(spot.likes ?? []).length}
          </button>
          <button
            type="button"
            className={beautiful ? styles.reactActive : styles.reactBtn}
            onClick={handleBeautiful}
            title="Beautiful"
          >
            ✨ {(spot.beautiful ?? []).length}
          </button>
          <button
            type="button"
            className={cool ? styles.reactActive : styles.reactBtn}
            onClick={handleCool}
            title="Cool"
          >
            😎 {(spot.cool ?? []).length}
          </button>
          <button
            type="button"
            className={sweetPhoto ? styles.reactActive : styles.reactBtn}
            onClick={handleSweetPhoto}
            title="Sweet Photo"
          >
            📷 {(spot.sweetPhoto ?? []).length}
          </button>
          <span className={styles.views}>👁 {spot.viewCount}</span>
        </div>

        {isAuthenticated && currentUser && favoriteLists.filter((f) => f.userId === currentUser.id).length > 0 && (
          <div className={styles.favorites}>
            <span className={styles.favLabel}>Add to list:</span>
            {favoriteLists
              .filter((f) => f.userId === currentUser.id)
              .map((list) => (
                <button
                  key={list.id}
                  type="button"
                  className={inList(list.id) ? styles.favActive : styles.favBtn}
                  onClick={() => toggleFavorite(list.id)}
                >
                  {list.name} {inList(list.id) ? '✓' : '+'}
                </button>
              ))}
          </div>
        )}

        <div id="comments" ref={commentsSectionRef} className={styles.commentsSection}>
          <h2>Comments</h2>
          {spot.comments.length > 0 ? (
            <CommentList
              comments={spot.comments}
              getAuthor={getAuthor}
              onReply={handleReplyToComment}
              activeReplyToCommentId={replyTarget?.id}
            />
          ) : (
            <p className={styles.emptyComments}>No comments yet.</p>
          )}
          <form onSubmit={handleSubmitComment} className={styles.commentForm}>
            {replyTarget && (
              <div className={styles.replyBanner}>
                <span>Replying to {replyTargetAuthor?.displayName ?? 'Unknown'}</span>
                <button type="button" onClick={clearReplyTarget}>Cancel</button>
              </div>
            )}
            <input
              ref={commentInputRef}
              type="text"
              placeholder={replyTarget ? 'Write a reply...' : 'Add a comment...'}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className={styles.commentInput}
            />
            <button
              type="submit"
              className={styles.commentSubmit}
              disabled={commentSubmitting || (isAuthenticated && !commentText.trim())}
            >
              {commentSubmitting ? 'Posting...' : replyTarget ? 'Reply' : 'Post'}
            </button>
          </form>
        </div>
      </article>
      {showAuthModal && <AuthRequiredModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}
